import React, { useState, useEffect, useRef, useCallback } from 'react';
import { crmService } from '../../services/dataService';

const CustomerPicker = ({ restaurantId, value, onChange, disabled }) => {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [open, setOpen]             = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addData, setAddData]       = useState({ name: '', phone: '', countryCode: '+91', email: '', is_whatsapp: false });
  const [saving, setSaving]         = useState(false);
  const wrapRef    = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setShowAddForm(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    const { data } = await crmService.searchCustomers(restaurantId, q);
    setResults(data || []);
  }, [restaurantId]);

  // Debounced search when query or open changes
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!open) return;
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, doSearch]);

  const handleFocus = () => {
    setOpen(true);
    doSearch(query);
  };

  const selectCustomer = (c) => {
    onChange(c);
    setOpen(false);
    setQuery('');
    setShowAddForm(false);
  };

  const handleAdd = async () => {
    if (!addData.name.trim()) return;
    setSaving(true);
    const { data, error } = await crmService.createCustomer(restaurantId, {
      name: addData.name.trim(),
      phone: addData.phone.trim() ? `${addData.countryCode}${addData.phone.trim()}` : null,
      email: addData.email.trim() || null,
      is_whatsapp: addData.is_whatsapp,
      discount_type: 'NONE',
      discount_value: 0,
    });
    setSaving(false);
    if (!error && data) {
      selectCustomer(data);
      setAddData({ name: '', phone: '', countryCode: '+91', email: '', is_whatsapp: false });
    }
  };

  const discountLabel = (c) => {
    if (!c || c.discount_type === 'NONE' || !c.discount_value) return null;
    return c.discount_type === 'PERCENT'
      ? `${c.discount_value}% off`
      : `₹${c.discount_value} off`;
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: '14px' }}>
      <label style={{
        display: 'block', fontSize: '0.82rem', fontWeight: '700',
        color: '#555', marginBottom: '5px',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        Customer (optional)
      </label>

      {value ? (
        /* Selected customer pill */
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '9px 14px', borderRadius: '10px',
          background: '#f0fdf4', border: '1.5px solid #bbf7d0',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#166534' }}>{value.name}</div>
            <div style={{ fontSize: '0.78rem', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
              {value.phone && <span>{value.phone}</span>}
              {value.is_whatsapp && value.phone && (
                <span style={{ background: '#dcfce7', color: '#166534', padding: '1px 7px', borderRadius: '10px', fontWeight: '700', fontSize: '0.7rem' }}>WA</span>
              )}
              {discountLabel(value) && (
                <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 8px', borderRadius: '10px', fontWeight: '700', fontSize: '0.72rem' }}>
                  {discountLabel(value)} auto-applied
                </span>
              )}
            </div>
          </div>
          {!disabled && (
            <button
              onClick={() => onChange(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: '700', fontSize: '1.2rem', padding: '0 4px', lineHeight: 1 }}
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={handleFocus}
            disabled={disabled}
            style={{
              width: '100%', padding: '9px 14px', borderRadius: '10px',
              border: '1.5px solid #e5e7eb', fontSize: '0.9rem', outline: 'none',
              boxSizing: 'border-box', fontFamily: 'inherit',
              background: disabled ? '#f9fafb' : 'white',
            }}
          />

          {open && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
              background: 'white', borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: '1px solid #e5e7eb', zIndex: 2000,
              maxHeight: '280px', overflowY: 'auto',
            }}>
              {/* Search results */}
              {results.length === 0 && !showAddForm && (
                <div style={{ padding: '12px 16px', color: '#aaa', fontSize: '0.85rem', textAlign: 'center' }}>
                  {query ? 'No customers found' : 'Type to search...'}
                </div>
              )}
              {results.map(c => (
                <div
                  key={c.id}
                  onClick={() => selectCustomer(c)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333' }}>
                      {c.name}
                      {c.is_whatsapp && c.phone && <span style={{ marginLeft: '6px', fontSize: '0.72rem', background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: '8px', fontWeight: '700' }}>WA</span>}
                    </div>
                    {c.phone && <div style={{ fontSize: '0.78rem', color: '#888' }}>{c.phone}</div>}
                  </div>
                  {discountLabel(c) && (
                    <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.72rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', flexShrink: 0, marginLeft: '8px' }}>
                      {discountLabel(c)}
                    </span>
                  )}
                </div>
              ))}

              {/* Add new toggle */}
              {!showAddForm ? (
                <div
                  onClick={() => setShowAddForm(true)}
                  style={{ padding: '10px 16px', cursor: 'pointer', color: '#FF4444', fontWeight: '700', fontSize: '0.85rem', borderTop: results.length > 0 ? '1px solid #f3f4f6' : 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onMouseOver={e => e.currentTarget.style.background = '#fff5f5'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                >
                  + Add New Customer
                </div>
              ) : (
                <div style={{ padding: '12px 14px', borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#333', marginBottom: '8px' }}>New Customer</div>
                  {[{ key: 'name', placeholder: 'Name *' }, { key: 'email', placeholder: 'Email' }].map(({ key, placeholder }) => (
                    <input
                      key={key}
                      type="text"
                      placeholder={placeholder}
                      value={addData[key]}
                      onChange={e => setAddData(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: '7px', border: '1.5px solid #e5e7eb', fontSize: '0.85rem', outline: 'none', marginBottom: '6px', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  ))}
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <select
                      value={addData.countryCode}
                      onChange={e => setAddData(prev => ({ ...prev, countryCode: e.target.value }))}
                      style={{ padding: '7px 8px', borderRadius: '7px', border: '1.5px solid #e5e7eb', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', background: 'white', flexShrink: 0 }}
                    >
                      <option value="+91">🇮🇳 +91</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Phone number"
                      value={addData.phone}
                      onChange={e => setAddData(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '') }))}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: '7px', border: '1.5px solid #e5e7eb', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', cursor: 'pointer', fontSize: '0.85rem', color: '#555' }}>
                    <input
                      type="checkbox"
                      checked={addData.is_whatsapp}
                      onChange={e => setAddData(prev => ({ ...prev, is_whatsapp: e.target.checked }))}
                      style={{ width: '15px', height: '15px', accentColor: '#25D366' }}
                    />
                    WhatsApp number
                  </label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={handleAdd}
                      disabled={saving || !addData.name.trim()}
                      style={{ flex: 1, padding: '8px', borderRadius: '8px', background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', border: 'none', fontWeight: '700', fontSize: '0.85rem', cursor: saving || !addData.name.trim() ? 'not-allowed' : 'pointer', opacity: saving || !addData.name.trim() ? 0.6 : 1 }}
                    >
                      {saving ? 'Saving...' : 'Save & Select'}
                    </button>
                    <button
                      onClick={() => setShowAddForm(false)}
                      style={{ padding: '8px 14px', borderRadius: '8px', background: 'transparent', color: '#666', border: '1.5px solid #e5e7eb', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerPicker;
